import { useMemo } from 'react'
import { usePlaybooksData } from './UsePlaybooksData'
import { useBlocksData } from './UseBlocksData'
import type { ContentItem, ContentType } from '@/types/Content'
import type { SectionId } from '../components/content-manager/ContentSidebar'
import type { RawPreviewPlay } from '../api/queries/PlaybookQueries'
import type { RootBlockDTO } from '@/types/blocks'
import { CANVAS_ROOT_TYPES } from '@/types/blocks'

/** DTO for playbook data from API/database - uses Date from db types */
interface PlaybookDTO {
	id: string
	name: string
	thumbnail?: string | null
	updated_at?: Date | string
	team_id?: string | null
	is_starred?: boolean
	play_count?: number
	preview_plays?: RawPreviewPlay[]
}

interface UseContentDataOptions {
	section: SectionId
	folderId?: string | null
	teamId?: string | null
}

/**
 * Safely parse JSON with error handling
 * @param input - JSON string to parse
 * @param fallback - Fallback value if parsing fails
 * @returns Parsed value or fallback
 */
export function safeJsonParse<T>(input: string, fallback: T): T {
	try {
		return JSON.parse(input)
	} catch (error) {
		console.error('Invalid JSON in database, using fallback:', error)
		return fallback
	}
}

/**
 * Transform raw preview play data from DB into PlayPreview format
 * Merges concept/custom drawings and formation/custom players
 */
export function transformPreviewPlays(rawPlays?: RawPreviewPlay[]): import('@/types/Content').PlayPreview[] {
	if (!rawPlays || rawPlays.length === 0) {
		return []
	}

	return rawPlays.map(raw => {
		// Parse custom players if string, default to empty array
		const customPlayers = typeof raw.custom_players === 'string'
			? safeJsonParse(raw.custom_players, [])
			: (raw.custom_players || [])

		// Parse custom drawings if string, default to empty array
		const customDrawings = typeof raw.custom_drawings === 'string'
			? safeJsonParse(raw.custom_drawings, [])
			: (raw.custom_drawings || [])

		// Merge all drawings
		const drawings = [
			...(raw.concept_drawings || []),
			...customDrawings,
		]

		// Merge all players (formation + custom)
		const players = [
			...(raw.formation_players || []),
			...customPlayers,
		]

		return {
			id: raw.id,
			drawings,
			players,
		}
	})
}

/**
 * Convert Date or string to ISO string format
 */
function toISOString(date: Date | string | undefined): string {
	if (!date) return new Date().toISOString()
	return typeof date === 'string' ? date : date.toISOString()
}

/**
 * Map playbook data to unified ContentItem format
 */
function mapPlaybookToContent(playbook: PlaybookDTO): ContentItem {
	return {
		id: playbook.id,
		type: 'playbook' as ContentType,
		name: playbook.name,
		thumbnail: playbook.thumbnail ?? undefined,
		updatedAt: toISOString(playbook.updated_at),
		teamId: playbook.team_id ?? undefined,
		folderId: undefined,
		starred: playbook.is_starred ?? false,
		shared: false,
		playCount: playbook.play_count ?? 0,
		previewPlays: transformPreviewPlays(playbook.preview_plays),
	}
}

/**
 * Map block data (from block-content domain) to unified ContentItem format
 *
 * Note: API returns snake_case from database (updated_at, created_at, etc.)
 * but RootBlockDTO declares camelCase. We handle both for safety.
 */
function mapBlockToContent(block: RootBlockDTO): ContentItem {
	// Handle both snake_case (from API) and camelCase (from type)
	const rawBlock = block as RootBlockDTO & {
		updated_at?: Date | string
		created_at?: Date | string
		is_starred?: boolean
		folder_id?: string | null
		type?: string
		play_name?: string
		properties?: { name?: string }
	}

	// Get updated_at, preferring snake_case (actual API response)
	const rawUpdatedAt = rawBlock.updated_at ?? rawBlock.updatedAt
	const updatedAt = rawUpdatedAt
		? (typeof rawUpdatedAt === 'string' ? rawUpdatedAt : rawUpdatedAt.toISOString())
		: new Date().toISOString()

	// Get block type - API returns 'type', not 'blockType'
	const blockType = rawBlock.blockType ?? rawBlock.type ?? 'play'

	// Get name - plays use play_name, blocks store name in properties
	const name = rawBlock.name ?? rawBlock.play_name ?? rawBlock.properties?.name ?? ''

	return {
		id: block.id,
		type: blockType as ContentType,
		name,
		thumbnail: undefined,
		updatedAt,
		teamId: undefined, // Blocks get team through playbook
		folderId: (rawBlock.folderId ?? rawBlock.folder_id) ?? undefined,
		starred: rawBlock.isStarred ?? rawBlock.is_starred ?? false,
		shared: false,
	}
}

/** Block types that should be included in Content Manager (uses shared constant) */

export function useContentData({ section, folderId, teamId }: UseContentDataOptions) {
	// Folder view overrides section — show all content types, not just what a stale section would filter to
	const isFolderView = folderId !== null && folderId !== undefined
	const effectiveSection = isFolderView ? 'all' : section

	// Fetch playbooks with section and folder filtering
	const {
		playbooks,
		isLoading: playbooksLoading,
		fetchNextPage: fetchNextPlaybooksPage,
		hasNextPage: hasNextPlaybooksPage,
		isFetchingNextPage: isFetchingNextPlaybooksPage,
	} = usePlaybooksData(teamId ?? null, effectiveSection, folderId ?? null)

	// Map section to block query section
	const blockSection = effectiveSection === 'starred' ? 'starred'
		: effectiveSection === 'trash' ? 'trash'
		: 'all'

	// Fetch blocks (plays, formations, concepts, documents)
	const {
		blocks,
		isLoading: blocksLoading,
		fetchNextPage: fetchNextBlocksPage,
		hasNextPage: hasNextBlocksPage,
		isFetchingNextPage: isFetchingNextBlocksPage,
		toggleStar: toggleBlockStar,
		trash: trashBlock,
		restore: restoreBlock,
		moveToFolder: moveBlockToFolder,
		permanentDelete: permanentDeleteBlock,
	} = useBlocksData({
		section: blockSection,
		folderId,
		teamId,
		blockTypes: [...CANVAS_ROOT_TYPES, 'document'],
	})

	const content = useMemo<ContentItem[]>(() => {
		// Folder view: mixed content, server-side filtered
		if (isFolderView) {
			return [
				...playbooks.map(mapPlaybookToContent),
				...blocks.map(mapBlockToContent),
			]
		}

		// For content-type-specific sections, return only that type
		if (section === 'playbooks') {
			return playbooks.map(mapPlaybookToContent)
		}

		if (section === 'documents') {
			return blocks
				.filter(block => block.blockType === 'document')
				.map(mapBlockToContent)
		}

		// For mixed sections (recent, starred, shared, trash), merge
		return [
			...playbooks.map(mapPlaybookToContent),
			...blocks.map(mapBlockToContent),
		]
	}, [section, isFolderView, playbooks, blocks])

	// Determine fetch strategy based on section and folder
	const fetchNextPage = () => {
		if (isFolderView) {
			// Folder view: fetch both sources
			fetchNextPlaybooksPage()
			fetchNextBlocksPage()
		} else if (section === 'playbooks') {
			fetchNextPlaybooksPage()
		} else if (section === 'documents') {
			fetchNextBlocksPage()
		} else {
			// For mixed sections, fetch all
			fetchNextPlaybooksPage()
			fetchNextBlocksPage()
		}
	}

	const hasNextPage = isFolderView
		? hasNextPlaybooksPage || hasNextBlocksPage
		: section === 'playbooks'
			? hasNextPlaybooksPage
			: section === 'documents'
				? hasNextBlocksPage
				: hasNextPlaybooksPage || hasNextBlocksPage

	const isFetchingNextPage = isFetchingNextPlaybooksPage || isFetchingNextBlocksPage

	return {
		content,
		isLoading: playbooksLoading || blocksLoading,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		// Block operations for Content Manager actions
		blockOperations: {
			toggleStar: toggleBlockStar,
			trash: trashBlock,
			restore: restoreBlock,
			moveToFolder: moveBlockToFolder,
			permanentDelete: permanentDeleteBlock,
		},
	}
}
